import { CircleAlert } from "lucide-react";
import { useEffect, useRef, type KeyboardEvent, type ReactNode } from "react";

import { Button } from "core/shadcn/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "core/shadcn/components/ui/dialog";
import { cn } from "core/shadcn/lib/utils";

const TITLE_ROW_CLASS_NAME =
  "flex items-center gap-2 border-b border-border py-3.5 pr-12 pl-4 text-sm font-semibold text-foreground";

export interface ModalProps {
  open: boolean;
  /** Called on the close button, a click on the backdrop and Escape. */
  onClose?: () => void;
  title?: ReactNode;
  /** Controls at the end of the title row. They sit outside the title, so
   * they are not part of the dialog's accessible name. */
  titleActions?: ReactNode;
  /** Names the dialog for screen readers when there is no visible title. */
  "aria-label"?: string;
  footer?: ReactNode;
  children?: ReactNode;
  /** Show the close button in the corner. */
  closable?: boolean;
  /** Close when the backdrop is clicked. */
  maskClosable?: boolean;
  /** Close on Escape. */
  keyboard?: boolean;
  /** Classes for the dialog box, for example its width. Defaults to `w-96`. */
  className?: string;
  bodyClassName?: string;
  footerClassName?: string;
  titleClassName?: string;
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
  "data-test"?: string;
}

export function Modal({
  open,
  onClose,
  title,
  titleActions,
  "aria-label": ariaLabel,
  footer,
  children,
  closable = true,
  maskClosable = true,
  keyboard = true,
  className,
  bodyClassName,
  footerClassName,
  titleClassName,
  onKeyDown,
  "data-test": dataTest,
}: ModalProps) {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose?.()}>
      <DialogContent
        data-test={dataTest}
        aria-describedby={undefined}
        showCloseButton={closable}
        onPointerDownOutside={(event) =>
          !maskClosable && event.preventDefault()
        }
        onEscapeKeyDown={(event) => !keyboard && event.preventDefault()}
        onKeyDown={onKeyDown}
        // Focus the dialog itself, not its first field or button, as antd did.
        // A field that should take focus uses autoFocus, which wins over this.
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          if (event.target instanceof HTMLElement) {
            event.target.focus({ preventScroll: true });
          }
        }}
        className={cn(
          "flex w-96 flex-col gap-4 rounded-md p-0 text-xs text-subtle-foreground",
          className,
        )}
      >
        {title && titleActions ? (
          <div className={cn(TITLE_ROW_CLASS_NAME, titleClassName)}>
            {/* `!` because antd's unlayered heading color beats the row's. */}
            <DialogTitle className="!mb-0 min-w-0 flex-1 truncate text-sm !text-foreground">
              {title}
            </DialogTitle>
            {titleActions}
          </div>
        ) : title ? (
          <DialogTitle className={cn(TITLE_ROW_CLASS_NAME, titleClassName)}>
            {title}
          </DialogTitle>
        ) : (
          <DialogTitle className="sr-only">{ariaLabel}</DialogTitle>
        )}
        <div
          className={cn(
            "flex flex-col px-4",
            !footer && "pb-4",
            !title && "pt-4",
            bodyClassName,
          )}
        >
          {children}
        </div>
        {footer && (
          <div
            className={cn("flex justify-end gap-3 px-4 pb-4", footerClassName)}
          >
            {footer}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface DeleteModalProps extends Omit<ModalProps, "footer" | "onClose"> {
  cancelButtonText?: string;
  dataTestName?: string;
  deleteButtonText?: string;
  disableDelete?: boolean;
  error?: string;
  isDeleting: boolean;
  onCancel: () => void;
  onDelete: () => void;
}

export function DeleteModal({
  cancelButtonText = "Cancel",
  children,
  dataTestName = "",
  deleteButtonText = "Delete",
  disableDelete,
  error,
  isDeleting,
  onCancel,
  onDelete,
  title,
  ...props
}: DeleteModalProps) {
  // Enter confirms, once per opening, unless it comes from a field or a
  // button that handles Enter itself.
  const hasConfirmedRef = useRef(false);
  useEffect(() => {
    if (props.open) hasConfirmedRef.current = false;
  }, [props.open]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "Enter" || event.repeat || hasConfirmedRef.current) {
      return;
    }
    if (disableDelete || isDeleting) return;
    const { target } = event;
    if (
      target instanceof HTMLElement &&
      (target.isContentEditable ||
        target.closest("input, textarea, select, button, a"))
    ) {
      return;
    }
    hasConfirmedRef.current = true;
    onDelete();
  };

  return (
    <Modal
      {...props}
      onClose={onCancel}
      onKeyDown={handleKeyDown}
      title={
        <>
          <CircleAlert className="size-5 shrink-0 text-sb-red-500" />
          {title}
        </>
      }
      footer={
        <>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            disabled={isDeleting}
            data-test={`cancel-delete-${dataTestName}`}
          >
            {cancelButtonText}
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={onDelete}
            disabled={isDeleting || disableDelete}
            data-test={`confirm-delete-${dataTestName}`}
          >
            {deleteButtonText}
          </Button>
        </>
      }
    >
      <div className="text-sm leading-5 [&_b]:font-medium">{children}</div>
      {error && <div className="text-sb-red-500">{error}</div>}
    </Modal>
  );
}
